import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import {
  ArrowUturnLeftIcon,
  ArrowDownTrayIcon,
  BanknotesIcon,
  BookOpenIcon,
  BriefcaseIcon,
  CheckBadgeIcon,
  ClockIcon,
  ClipboardDocumentListIcon,
  DocumentTextIcon,
  EnvelopeIcon,
  ExclamationTriangleIcon,
  HeartIcon,
  IdentificationIcon,
  LockClosedIcon,
  MapPinIcon,
  PhoneIcon,
  PlusIcon,
  UserGroupIcon,
} from "@heroicons/react/24/outline";

import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import { PMField } from "../../../components/utils/PMField";
import { PMDialog } from "../../../components/utils/PMDialog";
import { FormikSaveBar } from "../../../components/utils/FormikSaveBar";
import { PromangRichTextEditor } from "../../../components/utils/PromangRichTextEditor";
import { ResourceLoading } from "../../../components/utils/ResourceLoading";
import { PMNotice } from "../../../components/utils/notifications";
import UserChildrenTable from "../../../components/dashboard/company/users/user-children-table";
import UserNextOfKinTable from "../../../components/dashboard/company/users/user-next-of-kin-table";
import RoleOnly from "../../../routes/roleOnly";
import { dateToString, dispatchWithToast } from "../../../components/Utils";
import {
  fetchProfileAsync,
  updateProfileAsync,
  fetchProfileVersionsAsync,
  revertProfileVersionAsync,
} from "../../../slices/user";

const SCALAR_FIELDS = [
  "name",
  "private_email",
  "mobile",
  "mobile_work",
  "address",
  "postal",
  "place",
  "birth_date",
  "social_security",
  "account_number",
];

const CHILD_KEYS = [
  "id",
  "name",
  "birth_date",
  "sole_custody",
  "extended_rights",
  "active",
  "delete",
];
const KIN_KEYS = [
  "id",
  "name",
  "relationship_type",
  "phone_number",
  "address",
  "active",
  "delete",
];

const pick = (obj, keys) =>
  keys.reduce((acc, key) => {
    if (obj?.[key] !== undefined) acc[key] = obj[key];
    return acc;
  }, {});

const cleanRows = (rows, keys) =>
  (Array.isArray(rows) ? rows : []).map((row) => pick(row, keys));

/** Card-style section wrapper matching the company user-info layout. */
const Section = ({ icon: Icon, title, hint, children, actions }) => (
  <div className="mt-5 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(16,28,46,0.05)]">
    <div className="flex items-center gap-2.5 border-b border-[#EEF1F5] px-5 py-3.5">
      {Icon ? (
        <span className="flex h-[30px] w-[30px] flex-none items-center justify-center rounded-[9px] bg-[#EAF3FD] text-[#1463C7]">
          <Icon className="h-4 w-4" />
        </span>
      ) : null}
      <div className="min-w-0">
        <h3 className="text-sm font-bold text-slate-900">{title}</h3>
        {hint ? <p className="text-xs text-slate-500">{hint}</p> : null}
      </div>
      {actions ? <div className="ml-auto">{actions}</div> : null}
    </div>
    <div className="p-5">{children}</div>
  </div>
);

const displayValue = (value, t) => {
  if (value === null || value === undefined || value === "") {
    return <span className="text-slate-400">{t("user_profile.empty_value")}</span>;
  }
  if (typeof value === "boolean") return value ? t("Yes") : t("No");
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

/** Read-only definition row used in the employment tab and diff dialog. */
const InfoRow = ({ label, children }) => (
  <div className="grid grid-cols-1 gap-1 border-b border-slate-100 py-3 sm:grid-cols-3">
    <dt className="text-sm font-medium text-slate-500">{label}</dt>
    <dd className="text-sm text-slate-900 sm:col-span-2">{children}</dd>
  </div>
);

export const UserProfile = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const currentUser = useSelector((state) => state.session.currentUser);

  const [profile, setProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(
    location.hash === "#email" ? "email" : "profile"
  );

  // Reveal state for "addable" (empty + non-admin) fields.
  const [revealedFields, setRevealedFields] = useState({});

  // Approval / diff dialog state.
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingValues, setPendingValues] = useState(null);
  const [diffRows, setDiffRows] = useState([]);
  const [collectionChanges, setCollectionChanges] = useState({
    children: false,
    kin: false,
  });

  // History / versions dialog state.
  const [historyOpen, setHistoryOpen] = useState(false);
  const [versions, setVersions] = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [revertingId, setRevertingId] = useState(null);

  const baselineRef = useRef(null);

  const loadProfile = async () => {
    setIsLoading(true);
    const res = await dispatch(fetchProfileAsync());
    const data = res?.payload;
    if (data) {
      setProfile(data);
      baselineRef.current = data;
    } else {
      toast.error(t("user_profile.load_failed"));
    }
    setIsLoading(false);
    return data;
  };

  const persistCollectionRows = useCallback(
    async (fieldName, rows) => {
      const keys = fieldName === "user_children" ? CHILD_KEYS : KIN_KEYS;
      const res = await dispatch(
        updateProfileAsync({ user: { [fieldName]: cleanRows(rows, keys) } })
      );
      const data = res?.payload;
      if (!data || data.error || data.errors) {
        throw new Error(data?.errors?.[0] || t("user_profile.related_records.save_failed"));
      }
      setProfile(data);
      baselineRef.current = data;
      return data[fieldName] || [];
    },
    [dispatch, t]
  );

  useEffect(() => {
    loadProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (location.hash === "#email") setActiveTab("email");
  }, [location.hash]);

  const permissions = profile?.permissions || {};
  const fieldPerms = permissions.fields || {};
  const isAdmin = !!permissions.is_admin;

  const employmentType = profile?.employment_type;
  const isEmployee = !!profile?.is_employee;
  const isHired = !!profile?.is_hired;
  const employment = profile?.employment || null;

  const initialValues = useMemo(
    () => ({
      id: profile?.id,
      name: profile?.name || "",
      email: profile?.email || "",
      private_email: profile?.private_email || "",
      mobile: profile?.mobile || "",
      mobile_work: profile?.mobile_work || "",
      address: profile?.address || "",
      postal: profile?.postal || "",
      place: profile?.place || "",
      birth_date: profile?.birth_date || "",
      social_security: profile?.social_security || "",
      account_number: profile?.account_number || "",
      user_children: (profile?.user_children || []).map((row) => ({ ...row })),
      user_next_of_kins: (profile?.user_next_of_kins || []).map((row) => ({ ...row })),
      submit: null,
    }),
    [profile]
  );

  const buildScalarDiff = (baseline, values) => {
    const rows = [];
    SCALAR_FIELDS.forEach((field) => {
      const before = baseline?.[field] ?? "";
      const after = values?.[field] ?? "";
      if (String(before ?? "") !== String(after ?? "")) {
        rows.push({ field, from: before, to: after });
      }
    });
    return rows;
  };

  const collectionChanged = (baselineRows, currentRows, keys) =>
    JSON.stringify(cleanRows(baselineRows, keys)) !==
    JSON.stringify(cleanRows(currentRows, keys));

  const handleSubmit = (values, helpers) => {
    const baseline = baselineRef.current || {};
    const rows = buildScalarDiff(baseline, values);

    helpers.setSubmitting(false);

    if (!rows.length) {
      toast(t("user_profile.no_changes"));
      return;
    }

    setDiffRows(rows);
    setCollectionChanges({ children: false, kin: false });
    setPendingValues(values);
    setConfirmOpen(true);
  };

  const handleConfirmSave = () => {
    const v = pendingValues;
    if (!v) return;

    const userPayload = {
      name: v.name,
      private_email: v.private_email,
      mobile: v.mobile,
      mobile_work: v.mobile_work,
      address: v.address,
      postal: v.postal,
      place: v.place,
      birth_date: v.birth_date,
      social_security: v.social_security,
      account_number: v.account_number,
    };

    // Nested collections persist immediately from the dialog.

    dispatchWithToast(
      dispatch(updateProfileAsync({ user: userPayload })),
      (data) => {
        setProfile(data);
        baselineRef.current = data;
        setRevealedFields({});
        const ignored = data?.ignored_fields || [];
        if (ignored.length) {
          toast(
            `${t("user_profile.ignored_fields_notice")}: ${ignored
              .map((f) => t(`user_profile.field.${f}`))
              .join(", ")}`,
            { icon: "🔒", duration: 6000 }
          );
        }
      },
      () => setConfirmOpen(false)
    );
  };

  const openHistory = async () => {
    setHistoryOpen(true);
    setVersionsLoading(true);
    const res = await dispatch(fetchProfileVersionsAsync());
    setVersions(res?.payload?.data || []);
    setVersionsLoading(false);
  };

  const handleRevert = (versionId) => {
    setRevertingId(versionId);
    dispatchWithToast(
      dispatch(revertProfileVersionAsync({ versionId })),
      (data) => {
        setProfile(data);
        baselineRef.current = data;
      },
      async () => {
        setRevertingId(null);
        // Refresh the version list so the new revert entry shows up.
        const res = await dispatch(fetchProfileVersionsAsync());
        setVersions(res?.payload?.data || []);
      }
    );
  };

  /** Permission-driven field: editable input, locked read-only, or add affordance. */
  const renderControlledField = (formik, fieldName, label, type = "text") => {
    const perm = fieldPerms[fieldName] || {};
    const revealed = !!revealedFields[fieldName];

    if (perm.editable || revealed) {
      return <PMField name={fieldName} type={type} label={label} {...formik} />;
    }

    if (perm.locked) {
      return (
        <div>
          <PMField name={fieldName} type={type} label={label} disabled {...formik} />
          <p className="mt-1 flex items-center gap-1 text-xs text-slate-500">
            <LockClosedIcon className="h-3.5 w-3.5" />
            {t("user_profile.field_locked_hint")}
          </p>
        </div>
      );
    }

    if (perm.addable) {
      return (
        <div>
          <label className="block text-sm font-medium text-gray-700">{label}</label>
          <button
            type="button"
            onClick={() =>
              setRevealedFields((prev) => ({ ...prev, [fieldName]: true }))
            }
            className="mt-2 inline-flex items-center gap-2 rounded-md border border-dashed border-[#BFDCF7] bg-[#EAF3FD] px-3 py-2 text-sm font-semibold text-[#0B3A78] transition-colors hover:bg-[#dcebfb]"
          >
            <PlusIcon className="h-4 w-4" />
            {t("user_profile.add_field", { field: label })}
          </button>
        </div>
      );
    }

    // Fallback: read-only display when no permission flags are provided.
    return <PMField name={fieldName} type={type} label={label} disabled {...formik} />;
  };

  const employmentStatusBanner = () => {
    if (!isEmployee && !isHired) return null;
    const employee = employmentType === "employee" || isEmployee;
    return (
      <div
        className={`mt-6 flex items-center gap-3 rounded-2xl border p-4 shadow-sm ${
          employee
            ? "border-[#BFDCF7] bg-gradient-to-br from-[#EAF3FD] to-[#F2F8FE]"
            : "border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50"
        }`}
      >
        <span
          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${
            employee ? "bg-[#1463C7]/10 text-[#1463C7]" : "bg-amber-100 text-amber-600"
          }`}
        >
          {employee ? (
            <CheckBadgeIcon className="h-6 w-6" />
          ) : (
            <BriefcaseIcon className="h-6 w-6" />
          )}
        </span>
        <div className="min-w-0">
          <p className="text-sm font-bold text-slate-900">
            {employee
              ? t("user_profile.banner.employee_title")
              : t("user_profile.banner.hired_title")}
          </p>
          <p className="text-sm text-slate-600">
            {employee
              ? t("user_profile.banner.employee_body")
              : t("user_profile.banner.hired_body")}
          </p>
        </div>
        <span
          className={`ml-auto rounded-full px-3 py-1 text-xs font-semibold ${
            employee
              ? "bg-[#1463C7] text-white"
              : "bg-amber-500 text-white"
          }`}
        >
          {employee
            ? t("company.users.user_type.employee")
            : t("company.users.user_type.hired")}
        </span>
      </div>
    );
  };

  const renderProfileForm = () => (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={Yup.object({
        name: Yup.string().required(t("user_profile.validation.name_required")),
      })}
      onSubmit={handleSubmit}
    >
      {(formik) => (
        <Form>
          {/* Contact information */}
          <Section icon={PhoneIcon} title={t("user_profile.section.contact")}>
            <div className="grid grid-cols-6 gap-6">
              <div className="col-span-6 sm:col-span-3">
                <PMField name="name" type="text" label={t("user_profile.field.name")} {...formik} />
              </div>
              <div className="col-span-6 sm:col-span-3">
                <PMField
                  name="email"
                  type="text"
                  label={t("user_profile.field.email")}
                  disabled
                  {...formik}
                />
                <p className="mt-1 text-xs text-slate-500">
                  {t("user_profile.email_locked_hint")}
                </p>
              </div>
              <div className="col-span-6 sm:col-span-3">
                <PMField
                  name="private_email"
                  type="email"
                  label={t("user_profile.field.private_email")}
                  {...formik}
                />
                <p className="mt-1 text-xs text-slate-500">
                  {t("user_profile.private_email_hint")}
                </p>
              </div>
              <div className="col-span-6 sm:col-span-3">
                <PMField
                  name="mobile"
                  type="text"
                  label={t("user_profile.field.mobile")}
                  {...formik}
                />
              </div>
              <div className="col-span-6 sm:col-span-3">
                <PMField
                  name="mobile_work"
                  type="text"
                  label={t("user_profile.field.mobile_work")}
                  {...formik}
                />
              </div>
            </div>
          </Section>

          {/* Address */}
          <Section icon={MapPinIcon} title={t("user_profile.section.address")}>
            <div className="grid grid-cols-6 gap-6">
              <div className="col-span-6">
                <PMField
                  name="address"
                  type="text"
                  label={t("user_profile.field.address")}
                  {...formik}
                />
              </div>
              <div className="col-span-6 sm:col-span-2">
                <PMField
                  name="postal"
                  type="text"
                  label={t("user_profile.field.postal")}
                  {...formik}
                />
              </div>
              <div className="col-span-6 sm:col-span-4">
                <PMField
                  name="place"
                  type="text"
                  label={t("user_profile.field.place")}
                  {...formik}
                />
              </div>
            </div>
          </Section>

          {/* Sensitive / permission-controlled details */}
          <Section
            icon={IdentificationIcon}
            title={t("user_profile.section.personal_details")}
            hint={t("user_profile.section.personal_details_hint")}
          >
            <div className="grid grid-cols-6 gap-6">
              <div className="col-span-6 sm:col-span-2">
                {renderControlledField(
                  formik,
                  "birth_date",
                  t("user_profile.field.birth_date"),
                  "date"
                )}
              </div>
              <div className="col-span-6 sm:col-span-2">
                {renderControlledField(
                  formik,
                  "social_security",
                  t("user_profile.field.social_security"),
                  "text"
                )}
              </div>
              <div className="col-span-6 sm:col-span-2">
                {renderControlledField(
                  formik,
                  "account_number",
                  t("user_profile.field.account_number"),
                  "text"
                )}
              </div>
            </div>
          </Section>

          {/* Children */}
          <Section icon={UserGroupIcon} title={t("user_profile.section.children")}>
            <UserChildrenTable
              name="user_children"
              syncKey={profile?.version}
              onPersistRows={persistCollectionRows}
              {...formik}
            />
          </Section>

          {/* Next of kin */}
          <Section icon={HeartIcon} title={t("user_profile.section.next_of_kin")}>
            <UserNextOfKinTable
              name="user_next_of_kins"
              syncKey={profile?.version}
              onPersistRows={persistCollectionRows}
              {...formik}
            />
          </Section>

          <FormikSaveBar {...formik} isDirty={formik.dirty} />
        </Form>
      )}
    </Formik>
  );

  const renderEmploymentTab = () => {
    if (!isEmployee) {
      return (
        <div className="mt-5">
          <PMNotice
            severity="info"
            title={t("user_profile.employment.not_employee_title")}
            description={t("user_profile.employment.not_employee_body")}
          />
        </div>
      );
    }

    if (!employment) {
      return (
        <div className="mt-5">
          <PMNotice
            severity="info"
            title={t("user_profile.employment.none_title")}
            description={t("user_profile.employment.none_body")}
          />
        </div>
      );
    }

    const wageText = [employment.wages, employment.wages_unit]
      .filter(Boolean)
      .join(" ");
    const worktime = [employment.worktime_regular_from, employment.worktime_regular_too]
      .filter(Boolean)
      .join(" - ");

    return (
      <>
        <Section
          icon={BriefcaseIcon}
          title={t("user_profile.employment.title")}
          hint={t("user_profile.employment.read_only_hint")}
        >
          <dl>
            <InfoRow label={t("user_profile.employment.working_posistion")}>
              {displayValue(employment.working_posistion, t)}
            </InfoRow>
            <InfoRow label={t("user_profile.employment.employee_number")}>
              {displayValue(employment.employee_number, t)}
            </InfoRow>
            <InfoRow label={t("user_profile.employment.employment_starts")}>
              {displayValue(employment.employment_starts, t)}
            </InfoRow>
            <InfoRow label={t("user_profile.employment.agreement_type")}>
              {displayValue(employment.agreement_type, t)}
            </InfoRow>
            <InfoRow label={t("user_profile.employment.workplace")}>
              {displayValue(employment.workplace, t)}
            </InfoRow>
            <InfoRow label={t("user_profile.employment.wages")}>
              {displayValue(wageText, t)}
            </InfoRow>
            <InfoRow label={t("user_profile.employment.worktime")}>
              {displayValue(worktime, t)}
            </InfoRow>
            <InfoRow label={t("user_profile.employment.signed_at")}>
              {displayValue(employment.signed_at, t)}
            </InfoRow>
            <InfoRow label={t("user_profile.employment.signed_place")}>
              {displayValue(employment.signed_place, t)}
            </InfoRow>
          </dl>
        </Section>

        <Section icon={DocumentTextIcon} title={t("user_profile.employment.contract_title")}>
          {employment.has_signed_contract && employment.signed_contract_url ? (
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-2 text-sm font-medium text-emerald-700">
                <CheckBadgeIcon className="h-5 w-5" />
                {t("user_profile.employment.contract_signed")}
              </div>
              <a
                href={employment.signed_contract_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-lg bg-[#1463C7] px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#0B3A78]"
              >
                <ArrowDownTrayIcon className="h-4 w-4" />
                {t("user_profile.employment.download_contract")}
              </a>
            </div>
          ) : (
            <PMNotice
              severity="warning"
              title={t("user_profile.employment.contract_unsigned_title")}
              description={t("user_profile.employment.contract_unsigned_body")}
            />
          )}
        </Section>
      </>
    );
  };

  const quickLinks = (
    <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
      <RoleOnly roles={["payslips", "payslips_admin"]} feature="payslips">
        <button
          type="button"
          onClick={() => navigate("/dashboard/payslips")}
          className="group flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:border-[#BFDCF7] hover:shadow-md"
        >
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600">
            <BanknotesIcon className="h-6 w-6" />
          </span>
          <div className="min-w-0">
            <p className="text-sm font-bold text-slate-900">
              {t("user_profile.quicklinks.payslips_title")}
            </p>
            <p className="text-xs text-slate-500">
              {t("user_profile.quicklinks.payslips_body")}
            </p>
          </div>
        </button>
      </RoleOnly>

        <button
          type="button"
          onClick={() => navigate("/dashboard/quality/employee-handbook")}
          className="group flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:border-[#BFDCF7] hover:shadow-md"
        >
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
            <BookOpenIcon className="h-6 w-6" />
          </span>
          <div className="min-w-0">
            <p className="text-sm font-bold text-slate-900">
              {t("user_profile.quicklinks.handbook_title")}
            </p>
            <p className="text-xs text-slate-500">
              {t("user_profile.quicklinks.handbook_body")}
            </p>
          </div>
        </button>
    </div>
  );

  const handbookSection = (
      <div className="mt-6">
        <div className="rounded-2xl border border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="rounded-lg bg-blue-100 p-3">
                <BookOpenIcon className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {t("Personalhåndbok")}
                </h3>
                <p className="text-sm text-gray-600">
                  {t("employee_handbook.forms_tab_subtitle")}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => navigate("/dashboard/quality/employee-handbook")}
              className="flex items-center space-x-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors duration-200 hover:bg-blue-700"
            >
              <span>{t("Open")}</span>
              <BookOpenIcon className="h-4 w-4" />
            </button>
          </div>

          {/* Mirror the handbook overview cards (documents, chapters, forms, events). */}
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-lg border border-gray-200 bg-white p-4 transition-colors duration-200 hover:border-blue-300">
              <div className="flex items-center space-x-3">
                <DocumentTextIcon className="h-5 w-5 text-blue-600" />
                <div>
                  <h4 className="font-medium text-gray-900">
                    {t("KS/HMS Documents")}
                  </h4>
                  <p className="text-xs text-gray-500">
                    {t("user_profile.handbook.documents_hint")}
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-gray-200 bg-white p-4 transition-colors duration-200 hover:border-blue-300">
              <div className="flex items-center space-x-3">
                <BookOpenIcon className="h-5 w-5 text-emerald-600" />
                <div>
                  <h4 className="font-medium text-gray-900">{t("Kapitler")}</h4>
                  <p className="text-xs text-gray-500">
                    {t("user_profile.handbook.chapters_hint")}
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-gray-200 bg-white p-4 transition-colors duration-200 hover:border-blue-300">
              <div className="flex items-center space-x-3">
                <ClipboardDocumentListIcon className="h-5 w-5 text-indigo-600" />
                <div>
                  <h4 className="font-medium text-gray-900">
                    {t("employee_handbook.forms_tab")}
                  </h4>
                  <p className="text-xs text-gray-500">
                    {t("user_profile.handbook.forms_hint")}
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-gray-200 bg-white p-4 transition-colors duration-200 hover:border-blue-300">
              <div className="flex items-center space-x-3">
                <ExclamationTriangleIcon className="h-5 w-5 text-rose-600" />
                <div>
                  <h4 className="font-medium text-gray-900">{t("Hendelser")}</h4>
                  <p className="text-xs text-gray-500">
                    {t("user_profile.handbook.events_hint")}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
  );

  const renderConfirmDialog = () => (
    <PMDialog
      title={t("user_profile.confirm.title")}
      open={confirmOpen}
      onClose={() => setConfirmOpen(false)}
      size="2xl"
    >
      <p className="text-sm text-slate-600">{t("user_profile.confirm.intro")}</p>

      {diffRows.length > 0 && (
        <dl className="mt-4">
          {diffRows.map((row) => (
            <InfoRow key={row.field} label={t(`user_profile.field.${row.field}`)}>
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded bg-rose-50 px-2 py-0.5 text-rose-700 line-through">
                  {displayValue(row.from, t)}
                </span>
                <span className="text-slate-400">→</span>
                <span className="rounded bg-emerald-50 px-2 py-0.5 font-medium text-emerald-700">
                  {displayValue(row.to, t)}
                </span>
              </div>
            </InfoRow>
          ))}
        </dl>
      )}

      {(collectionChanges.children || collectionChanges.kin) && (
        <ul className="mt-4 list-disc space-y-1 pl-5 text-sm text-slate-700">
          {collectionChanges.children && (
            <li>{t("user_profile.confirm.children_changed")}</li>
          )}
          {collectionChanges.kin && (
            <li>{t("user_profile.confirm.kin_changed")}</li>
          )}
        </ul>
      )}

      <div className="mt-6 flex justify-end gap-3">
        <button
          type="button"
          onClick={() => setConfirmOpen(false)}
          className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
        >
          {t("Cancel")}
        </button>
        <button
          type="button"
          onClick={handleConfirmSave}
          className="rounded-lg bg-[#1463C7] px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-[#0B3A78]"
        >
          {t("user_profile.confirm.confirm_button")}
        </button>
      </div>
    </PMDialog>
  );

  const renderVersionChanges = (changes) => {
    const entries = Object.entries(changes || {});
    if (!entries.length) {
      return (
        <p className="text-sm text-slate-400">{t("user_profile.history.no_changes")}</p>
      );
    }
    return (
      <ul className="space-y-1.5">
        {entries.map(([field, change]) => (
          <li key={field} className="text-sm text-slate-700">
            <span className="font-medium text-slate-900">
              {t(`user_profile.field.${field}`) === `user_profile.field.${field}`
                ? field.replace(/_/g, " ")
                : t(`user_profile.field.${field}`)}
            </span>
            <span className="mx-1 text-slate-400">:</span>
            <span className="rounded bg-rose-50 px-1.5 py-0.5 text-rose-700 line-through">
              {displayValue(change?.from, t)}
            </span>
            <span className="mx-1 text-slate-400">→</span>
            <span className="rounded bg-emerald-50 px-1.5 py-0.5 font-medium text-emerald-700">
              {displayValue(change?.to, t)}
            </span>
          </li>
        ))}
      </ul>
    );
  };

  const renderEmailTab = () => (
    <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-50 text-slate-700">
          <EnvelopeIcon className="h-5 w-5" />
        </div>
        <div>
          <h2 className="text-base font-semibold text-slate-900">
            {t("user_profile.email.signature_title")}
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            {t("user_profile.email.signature_help")}
          </p>
        </div>
      </div>
      <div className="mt-4">
        <PromangRichTextEditor
          value={profile?.email_signature_html || ""}
          onChange={(html) => setProfile((prev) => ({ ...prev, email_signature_html: html }))}
          placeholder={t("user_profile.email.signature_placeholder")}
          templatesEnabled={false}
          size="md"
          framed
        />
      </div>
      <div className="mt-4 flex justify-end">
        <button
          type="button"
          className="rounded-lg bg-[#1463C7] px-4 py-2 text-sm font-semibold text-white"
          onClick={() => {
            dispatchWithToast(
              dispatch(
                updateProfileAsync({
                  user: { email_signature_html: profile?.email_signature_html || "" },
                })
              ),
              (data) => {
                setProfile(data);
                baselineRef.current = data;
              }
            );
          }}
        >
          {t("user_profile.email.save")}
        </button>
      </div>
    </div>
  );

  const renderHistoryDialog = () => (
    <PMDialog
      title={t("user_profile.history.title")}
      open={historyOpen}
      onClose={() => setHistoryOpen(false)}
      size="3xl"
    >
      {versionsLoading ? (
        <ResourceLoading />
      ) : versions.length === 0 ? (
        <PMNotice
          severity="info"
          title={t("user_profile.history.empty_title")}
          description={t("user_profile.history.empty_body")}
        />
      ) : (
        <ul className="space-y-3">
          {versions.map((version) => (
            <li
              key={version.id}
              className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
            >
              <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
                <div className="flex items-center gap-2">
                  <span
                    className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                      version.is_revert
                        ? "bg-amber-100 text-amber-700"
                        : "bg-blue-100 text-blue-700"
                    }`}
                  >
                    {version.is_revert
                      ? t("user_profile.history.action_revert")
                      : t("user_profile.history.action_update")}
                  </span>
                  <span className="text-sm font-medium text-slate-900">
                    {version.changed_by?.name || t("histories.unknown_user")}
                  </span>
                </div>
                <time className="text-xs text-slate-500">
                  {dateToString(version.created_at)}
                </time>
              </div>

              {version.is_revert && version.reverted_from_version_id ? (
                <p className="mt-1 text-xs italic text-slate-500">
                  {t("user_profile.history.reverted_from", {
                    id: version.reverted_from_version_id,
                  })}
                </p>
              ) : null}

              <div className="mt-3">{renderVersionChanges(version.changes)}</div>

              {isAdmin && version.can_revert ? (
                <div className="mt-3 flex justify-end">
                  <button
                    type="button"
                    disabled={revertingId === version.id}
                    onClick={() => handleRevert(version.id)}
                    className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <ArrowUturnLeftIcon className="h-4 w-4" />
                    {t("user_profile.history.restore")}
                  </button>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </PMDialog>
  );

  const tabs = [
    { id: "profile", label: t("user_profile.tab.profile") },
    { id: "email", label: t("user_profile.tab.email") },
    ...(isEmployee
      ? [{ id: "employment", label: t("user_profile.tab.employment") }]
      : []),
  ];

  return (
    <DashboardHeader
      subtitle={currentUser?.name}
      actions={[]}
      title={t("user_profile.title")}
    >
      <div className="mx-auto mt-6 max-w-5xl px-4 sm:px-6">
        {isLoading ? (
          <ResourceLoading />
        ) : !profile ? (
          <PMNotice
            severity="error"
            title={t("user_profile.load_failed")}
            description={t("user_profile.load_failed_body")}
          />
        ) : (
          <>
            {employmentStatusBanner()}

            {quickLinks}

            <div className="mt-6 flex items-center justify-between gap-3 border-b border-slate-200">
              <nav className="flex gap-1">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveTab(tab.id)}
                    className={`rounded-t-lg px-4 py-2 text-sm font-semibold transition-colors ${
                      activeTab === tab.id
                        ? "border-b-2 border-[#1463C7] text-[#0B3A78]"
                        : "text-slate-500 hover:text-slate-700"
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </nav>
              <button
                type="button"
                onClick={openHistory}
                className="mb-1 inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50"
              >
                <ClockIcon className="h-4 w-4" />
                {t("user_profile.history.open")}
              </button>
            </div>

            {activeTab === "profile"
              ? renderProfileForm()
              : activeTab === "email"
                ? renderEmailTab()
                : renderEmploymentTab()}

            {handbookSection}

            {renderConfirmDialog()}
            {renderHistoryDialog()}
          </>
        )}
      </div>
    </DashboardHeader>
  );
};
